// fakturapp/src/shared/dialogs/ksef-xml-viewer-dialog/ksef-xml-viewer-dialog.component.ts

import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule} from '@angular/material/tooltip';
import { KsefMonitoringApiService } from 'shared/services/api/ksef-monitoring.api.service';
import { FakturniaHighlightComponent } from '@fakturnia/components/highlight';

@Component({
  standalone: true,
  imports: [
    CommonModule, 
    MatDialogModule, 
    MatButtonModule, 
    MatIconModule, 
    MatProgressSpinnerModule,
    MatTooltipModule,
    FakturniaHighlightComponent
  ],
  selector: 'app-ksef-xml-viewer-dialog',
  templateUrl: './ksef-xml-viewer-dialog.component.html',
  styleUrls: ['./ksef-xml-viewer-dialog.component.scss']
})
export class KsefXmlViewerDialogComponent implements OnInit {
  
  xml: string = '';
  isLoading = true;
  error: string | null = null;
  
  documentNumber: string = '';
  ksefNumber: string | null = null;
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { documentId: string },
    private dialogRef: MatDialogRef<KsefXmlViewerDialogComponent>,
    private _ksefApiService: KsefMonitoringApiService
  ) {}
  
  ngOnInit() {
    this.loadXml();
  }
  
  loadXml() {
    this.isLoading = true;
    this._ksefApiService.getDocumentXml(this.data.documentId).subscribe({
      next: (response) => {
        this.xml = response.data.xml;
        this.documentNumber = response.data.documentNumber;
        this.ksefNumber = response.data.ksefNumber;
        this.isLoading = false;
      },
      error: (error) => {
        this.error = error.error?.message || 'Błąd podczas ładowania XML';
        this.isLoading = false;
      }
    });
  }
  
  /**
   * Getter dla sformatowanego XML z większymi wcięciami
   */
  get formattedXml(): string {
    if (!this.xml) return '';
    
    try {
      // Parsuj XML i sformatuj z 8 spacjami wcięcia (bardziej widoczne)
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.xml, 'text/xml');
      
      // Sprawdź błędy parsowania
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        return this.xml; // Zwróć oryginalny jeśli błąd parsowania
      }
      
      // Sformatuj z wcięciami
      return this.formatXml(xmlDoc);
    } catch (e) {
      console.error('Błąd formatowania XML:', e);
      return this.xml; // Fallback do oryginalnego
    }
  }

  /**
   * Formatuje XML z czytelnymi wcięciami (8 spacji - bardzo widoczne)
   */
  private formatXml(xml: Document | Element, level: number = 0): string {
    const indent = '        '; // 8 spacji - bardziej widoczne wcięcia
    const padding = indent.repeat(level);
    let formatted = '';

    if (xml instanceof Document) {
      // Deklaracja XML
      formatted += '<?xml version="1.0" encoding="UTF-8"?>\n';
      if (xml.documentElement) {
        formatted += this.formatXml(xml.documentElement, level);
      }
      return formatted;
    }

    const element = xml as Element;
    const tagName = element.tagName;
    const attributes = element.attributes;
    const children = element.childNodes;

    // Otwórz tag
    formatted += padding + '<' + tagName;

    // Atrybuty
    for (let i = 0; i < attributes.length; i++) {
      const attr = attributes[i];
      formatted += ' ' + attr.name + '="' + attr.value + '"';
    }

    // Sprawdź czy ma dzieci
    const hasElementChildren = Array.from(children).some(child => child.nodeType === 1);
    const textContent = element.textContent?.trim() || '';

    if (children.length === 0) {
      // Samozamykający tag
      formatted += '/>\n';
    } else if (!hasElementChildren && textContent) {
      // Tylko tekst
      formatted += '>' + textContent + '</' + tagName + '>\n';
    } else {
      // Ma dzieci
      formatted += '>\n';

      // Dzieci
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child.nodeType === 1) { // Element node
          formatted += this.formatXml(child as Element, level + 1);
        }
      }

      // Zamknij tag
      formatted += padding + '</' + tagName + '>\n';
    }

    return formatted;
  }
  
  downloadXml() {
    const blob = new Blob([this.xml], { type: 'application/xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.documentNumber.replace(/\//g, '_')}.xml`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
  
  copyToClipboard() {
    navigator.clipboard.writeText(this.xml).then(() => {
      console.log('XML skopiowany do schowka');
    });
  }
  
  close() {
    this.dialogRef.close();
  }
}

export interface KsefXmlViewerDialogData {
  documentId: string;
}

